import { useMemo } from 'react'
import type { Control, FieldValues, Path } from 'react-hook-form'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { NumberInputField, PriceInputField } from '@/components/forms/FormComponents'
import { COST_MODE_LABELS, marginFigures, type CostMode } from '@/lib/productFormPayload'
import { cn } from '@/lib/utils'
import { ArrowUpRight } from 'lucide-react'
import { CollapsibleRow } from './CollapsibleRow'

const MODES: CostMode[] = ['recipe', 'amount', 'percent', 'none']

export interface ManualPreview {
  cost: number
  margin: number
  marginPct: number
}

export interface DormantEstimate {
  basis: 'amount' | 'percent'
  value: number | null
}

/** What the saved recipe looks like, read-only here (F-FE-N157). */
export interface RecipeSummary {
  loaded: boolean
  count: number
  /** Dine-in per-serving cost under the checkout completeness rule; null when uncosted. */
  cost: number | null
  uncosted: number
}

export interface ProductCostSectionProps<T extends FieldValues> {
  open: boolean
  onToggle: () => void
  control: Control<T>
  currencyPrefix: string
  formatCurrency: (n: number) => string
  price: number
  mode: CostMode
  onModeChange: (m: CostMode) => void
  /** Manual estimate (amount / percent modes) */
  manualPreview: ManualPreview | null
  /**
   * The recipe is EDITED in Costing & Recipes, not here (F-FE-N157). This
   * section only summarises it and links across; undefined while creating,
   * because a product has to exist before it can have a recipe.
   */
  recipeSummary: RecipeSummary | null
  onEditRecipe?: () => void
  /** A saved manual estimate that a recipe overrides (both set on the server). */
  dormantEstimate: DormantEstimate | null
  removeDormant: boolean
  onToggleRemoveDormant: () => void
}

function pct(n: number) {
  return `${n.toFixed(1)}%`
}

export function ProductCostSection<T extends FieldValues>(props: ProductCostSectionProps<T>) {
  const {
    open, onToggle, control, currencyPrefix, formatCurrency, price, mode, onModeChange,
    manualPreview, recipeSummary, onEditRecipe, dormantEstimate, removeDormant, onToggleRemoveDormant,
  } = props

  const figures = useMemo(() => {
    if (mode === 'recipe') return recipeSummary?.cost != null ? marginFigures(price, recipeSummary.cost) : null
    if (mode === 'amount' || mode === 'percent') return manualPreview ? marginFigures(price, manualPreview.cost) : null
    return null
  }, [mode, recipeSummary, manualPreview, price])

  const hasRecipe = (recipeSummary?.count ?? 0) > 0
  // F-MM-N13: while any ingredient has no cost, checkout books the manual
  // estimate, not the recipe (audit U-9).
  const estimateInUse = hasRecipe && (recipeSummary?.uncosted ?? 0) > 0

  const summary = (() => {
    if (mode === 'none') return <span>Not set</span>
    if (!figures) return <span>{COST_MODE_LABELS[mode]}</span>
    if (figures.loss) return <Badge variant="danger-surface">Costs more than it sells for</Badge>
    return (
      <span className="inline-flex items-center gap-2">
        <Badge variant="success-surface">{pct(figures.marginPct)} margin</Badge>
        <span>{COST_MODE_LABELS[mode]}</span>
      </span>
    )
  })()

  return (
    <CollapsibleRow id="product-cost-panel" title="Cost & profit" summary={summary} open={open} onToggle={onToggle}>
      <div className="space-y-2">
        <Label className="text-sm">How is the cost set?</Label>
        <div role="radiogroup" aria-label="Cost source" className="inline-flex max-w-full flex-wrap rounded-md border bg-background p-0.5">
          {MODES.map((m) => (
            <button
              key={m}
              type="button"
              role="radio"
              aria-checked={mode === m}
              onClick={() => onModeChange(m)}
              className={cn(
                'rounded px-3 py-1.5 text-xs font-medium transition-colors',
                mode === m ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted',
              )}
            >
              {m === 'recipe' ? 'From ingredients' : COST_MODE_LABELS[m]}
            </button>
          ))}
        </div>
        <p className="text-xs text-muted-foreground">
          {mode === 'recipe' && 'Stock is deducted on every sale and restored on void or refund. The ingredient cost is what Reports use.'}
          {(mode === 'amount' || mode === 'percent') && 'An assumed cost per unit, separate from the expenses ledger. Reports show it as projected margin.'}
          {mode === 'none' && 'Reports will show this item as having no cost. Use Costing & Recipes to set many items at once.'}
        </p>
      </div>

      {mode === 'recipe' && (
        <div className="space-y-2 rounded-lg border p-3">
          {!onEditRecipe ? (
            <p className="text-sm text-muted-foreground">
              Create the product first, then build its recipe in <span className="font-medium text-foreground">Menu → Costing &amp; Recipes</span>.
            </p>
          ) : !recipeSummary?.loaded ? (
            <p className="text-sm text-muted-foreground">Loading recipe…</p>
          ) : hasRecipe ? (
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm">
                <span className="font-medium">{recipeSummary!.count} ingredient{recipeSummary!.count === 1 ? '' : 's'}</span>
                {recipeSummary!.cost != null ? (
                  <span className="text-muted-foreground"> · {formatCurrency(recipeSummary!.cost)} per serving</span>
                ) : (
                  <span className="text-amber-700 dark:text-amber-300"> · not fully costed</span>
                )}
                {recipeSummary!.uncosted > 0 && (
                  <span className="text-amber-700 dark:text-amber-300"> · {recipeSummary!.uncosted} without a cost</span>
                )}
                {estimateInUse && dormantEstimate && (
                  <span className="block text-xs text-amber-700 dark:text-amber-300">
                    Checkout uses your estimate until every ingredient has a cost.
                  </span>
                )}
              </p>
              <Button type="button" variant="outline" size="sm" className="gap-1" onClick={onEditRecipe}>
                Edit recipe <ArrowUpRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          ) : (
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm text-muted-foreground">No ingredients yet.</p>
              <Button type="button" variant="outline" size="sm" className="gap-1" onClick={onEditRecipe}>
                Build the recipe <ArrowUpRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          )}
        </div>
      )}

      {(mode === 'amount' || mode === 'percent') && hasRecipe && (
        <p className="rounded-md border border-amber-300 bg-amber-50 p-2.5 text-xs text-amber-800 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-300">
          {estimateInUse ? (
            <>
              This item has a {recipeSummary!.count}-ingredient recipe with {recipeSummary!.uncosted} ingredient{recipeSummary!.uncosted === 1 ? '' : 's'} not yet costed. Checkout uses this estimate until every ingredient has a cost, then the recipe takes over. To cost it manually for good, remove the recipe in Costing &amp; Recipes.
            </>
          ) : (
            <>
              This item has a {recipeSummary!.count}-ingredient recipe, and the recipe is what checkout uses while it exists. To cost it manually instead, remove the recipe in Costing &amp; Recipes.
            </>
          )}
        </p>
      )}

      {mode === 'percent' && (
        <NumberInputField
          control={control}
          name={'projected_cost_percent' as Path<T>}
          label="Cost as % of price"
          min={0}
          max={100}
          description="35 means the cost is 35% of the sale price"
        />
      )}
      {mode === 'amount' && (
        <PriceInputField
          control={control}
          name={'projected_cost' as Path<T>}
          label="Cost per unit"
          currency={currencyPrefix}
        />
      )}

      {figures && (
        <div
          className={cn(
            'grid grid-cols-3 gap-px overflow-hidden rounded-lg border bg-border',
            figures.loss && 'border-destructive/40',
          )}
        >
          {[
            { k: 'Cost per sale', v: formatCurrency(figures.cost), tone: '' },
            { k: 'Profit per sale', v: formatCurrency(figures.profit), tone: figures.loss ? 'text-destructive' : 'text-success-fg' },
            { k: 'Margin', v: price > 0 ? pct(figures.marginPct) : '—', tone: figures.loss ? 'text-destructive' : 'text-success-fg' },
          ].map((cell) => (
            <div key={cell.k} className={cn('bg-muted/40 px-3 py-2.5', figures.loss && 'bg-destructive/5')}>
              <p className="text-xs text-muted-foreground">{cell.k}</p>
              <p className={cn('mt-0.5 text-base font-semibold tabular-nums', cell.tone)}>{cell.v}</p>
            </div>
          ))}
        </div>
      )}

      {mode === 'recipe' && dormantEstimate && estimateInUse && (
        <p className="text-xs text-muted-foreground">
          Your estimate of{' '}
          {dormantEstimate.basis === 'percent'
            ? `${dormantEstimate.value ?? 0}% of price`
            : formatCurrency(dormantEstimate.value ?? 0)}{' '}
          is what checkout uses until every ingredient in the recipe has a cost.
        </p>
      )}

      {mode === 'recipe' && dormantEstimate && !estimateInUse && (
        <p className={cn('text-xs', removeDormant ? 'text-destructive' : 'text-muted-foreground')}>
          {removeDormant ? (
            <>
              The manual estimate will be removed when you save.{' '}
              <button type="button" className="underline underline-offset-2" onClick={onToggleRemoveDormant}>
                Keep it
              </button>
            </>
          ) : (
            <>
              A manual estimate of{' '}
              {dormantEstimate.basis === 'percent'
                ? `${dormantEstimate.value ?? 0}% of price`
                : formatCurrency(dormantEstimate.value ?? 0)}{' '}
              is also saved but not used while a recipe exists.{' '}
              <button type="button" className="underline underline-offset-2 hover:text-foreground" onClick={onToggleRemoveDormant}>
                Remove it
              </button>
            </>
          )}
        </p>
      )}
    </CollapsibleRow>
  )
}
